import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { Category } from '@/types'
import { CategoryChip } from '@/components/menu/CategoryChip'
import { categoryColor } from '@/lib/menuCategoryStyle'
import { cn } from '@/lib/utils'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export type MenuCategoryBarProps = {
  categories: Category[]
  selectedCategory: string
  onSelect: (categoryId: string) => void
  /** Sum of product_count across categories — badge on "All". */
  totalCount?: number
  /**
   * `wrap` — multi-row grid (server tablet). Every category is visible.
   * `row` — single scroll row + overflow panel (checkout counter).
   */
  layout?: 'wrap' | 'row'
  className?: string
}

/**
 * Shared category picker used by Counter and Server menus.
 * Prefer `wrap` on floor tablets so categories aren't trapped in one scroll queue.
 */
export function MenuCategoryBar({
  categories,
  selectedCategory,
  onSelect,
  totalCount,
  layout = 'wrap',
  className,
}: MenuCategoryBarProps) {
  const [categoryPanelOpen, setCategoryPanelOpen] = useState(false)
  const [categoryBarOverflows, setCategoryBarOverflows] = useState(false)
  const [hiddenCategoryIds, setHiddenCategoryIds] = useState<string[]>([])
  const categoryBarRef = useRef<HTMLDivElement>(null)
  const categoryPanelWasOpenRef = useRef(false)

  useEffect(() => {
    if (layout !== 'row') return
    const bar = categoryBarRef.current
    if (!bar) return
    const el = bar.querySelector<HTMLElement>(
      `[data-category-id="${CSS.escape(selectedCategory)}"]`,
    )
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [selectedCategory, layout])

  useEffect(() => {
    if (layout !== 'row') return
    const bar = categoryBarRef.current
    if (!bar) return
    const check = () => {
      const overflows = bar.scrollWidth > bar.clientWidth + 1
      setCategoryBarOverflows(overflows)
      if (!overflows) {
        setHiddenCategoryIds([])
        setCategoryPanelOpen(false)
        return
      }
      const barRect = bar.getBoundingClientRect()
      const hidden: string[] = []
      bar.querySelectorAll<HTMLElement>('[data-category-id]').forEach((el) => {
        const r = el.getBoundingClientRect()
        if (r.left < barRect.left - 1 || r.right > barRect.right + 1) {
          const id = el.dataset.categoryId
          if (id) hidden.push(id)
        }
      })
      setHiddenCategoryIds(hidden)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(bar)
    bar.addEventListener('scroll', check, { passive: true })
    return () => {
      ro.disconnect()
      bar.removeEventListener('scroll', check)
    }
  }, [categories, layout])

  const allChip = (
    <CategoryChip
      categoryId="all"
      name="All"
      count={totalCount && totalCount > 0 ? totalCount : undefined}
      accent="hsl(220 14% 46%)"
      selected={selectedCategory === 'all'}
      onClick={() => onSelect('all')}
    />
  )

  const categoryChips = categories.map((category) => (
    <CategoryChip
      key={category.id}
      categoryId={category.id}
      name={category.name}
      count={typeof category.product_count === 'number' ? category.product_count : undefined}
      accent={categoryColor(category, category.name)}
      selected={selectedCategory === category.id}
      onClick={() => onSelect(category.id)}
    />
  ))

  if (layout === 'wrap') {
    return (
      <div
        className={cn(
          'shrink-0 border-b border-border bg-card px-3 py-2 sm:px-4',
          className,
        )}
      >
        <div className="flex flex-wrap gap-1.5">{allChip}{categoryChips}</div>
      </div>
    )
  }

  return (
    <Popover open={categoryPanelOpen} onOpenChange={setCategoryPanelOpen}>
      <PopoverAnchor asChild>
        <div
          className={cn(
            'flex shrink-0 items-center gap-2 px-3 py-2 sm:px-4',
            categoryBarOverflows && 'cursor-pointer',
            className,
          )}
          onPointerDownCapture={() => {
            categoryPanelWasOpenRef.current = categoryPanelOpen
          }}
          onClick={(e) => {
            if (!categoryBarOverflows) return
            if ((e.target as HTMLElement).closest('button')) return
            if (categoryPanelWasOpenRef.current) return
            setCategoryPanelOpen(true)
          }}
        >
          <div
            ref={categoryBarRef}
            className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {allChip}
            {categoryChips}
          </div>
          {categoryBarOverflows && (
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Show all categories"
                title="Show all categories"
                className={cn(
                  'flex h-10 shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2.5 text-muted-foreground shadow-sm transition hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  categoryPanelOpen && 'bg-muted/60 text-foreground',
                )}
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', categoryPanelOpen && 'rotate-180')}
                />
              </button>
            </PopoverTrigger>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        avoidCollisions={false}
        className="w-[var(--radix-popper-anchor-width)] max-h-[50vh] overflow-y-auto p-2 shadow-lg"
      >
        <div className="flex flex-wrap gap-1.5">
          {hiddenCategoryIds.includes('all') && (
            <CategoryChip
              categoryId="all"
              name="All"
              count={totalCount && totalCount > 0 ? totalCount : undefined}
              accent="hsl(220 14% 46%)"
              selected={selectedCategory === 'all'}
              onClick={() => {
                onSelect('all')
                setCategoryPanelOpen(false)
              }}
            />
          )}
          {categories
            .filter((category) => hiddenCategoryIds.includes(category.id))
            .map((category) => (
              <CategoryChip
                key={category.id}
                categoryId={category.id}
                name={category.name}
                count={
                  typeof category.product_count === 'number' ? category.product_count : undefined
                }
                accent={categoryColor(category, category.name)}
                selected={selectedCategory === category.id}
                onClick={() => {
                  onSelect(category.id)
                  setCategoryPanelOpen(false)
                }}
              />
            ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
